if ( typeof PickupAvailabilityCompact !== 'function' ) {

  class PickupAvailabilityCompact extends HTMLElement {

    constructor(){

      super();
      this.classList.add('active');

      this.storeSelector = document.querySelector('store-selector[data-main-selector]');
      if ( this.storeSelector ) {
        const STORE_CHANGED_EVENT = () => {
          this.#fetchAvailability(this.dataset.variantId, true);
        }
        this.storeSelector.addEventListener('storechanged', STORE_CHANGED_EVENT);
				const observer = new MutationObserver(()=>{
          this.storeSelector.removeEventListener('storechanged', STORE_CHANGED_EVENT);
					observer.disconnect();
				});
				observer.observe(this.parentElement, {
					attributes: false, childList: true, subtree: false
				});
      }

      if ( this.hasAttribute('data-static') ) {
        this.#fetchAvailability(this.dataset.variantId);
      } else {
        const widgetIntersection = (entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.unobserve(this);
          this.#fetchAvailability(this.dataset.variantId);
        }
        new IntersectionObserver(widgetIntersection.bind(this), {rootMargin: '0px 0px 50px 0px'}).observe(this);
      }
      
    }

    #fetchAvailability(variantId, forceFetch = false) {

      const pickupAvailabilityData = this.querySelector('[data-js-pickup-availability-data]');

      if ( pickupAvailabilityData && ! forceFetch ) {
        this.#renderPreview(pickupAvailabilityData);
      } else {

        const variantSectionUrl = `${this.dataset.baseUrl.endsWith('/')?this.dataset.baseUrl:`${this.dataset.baseUrl}/`}variants/${variantId}/?section_id=helper-pickup-availability-compact`;

        fetch(variantSectionUrl)
          .then(response => response.text())
          .then(text => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            if ( window.LetsDishLocationInventory && window.LetsDishLocationInventory.cacheSet ) {
              const map = {};
              sectionInnerHTML.querySelectorAll('.pickup-availability-alert[data-store]').forEach(elm => {
                map[elm.dataset.store] = !!elm.querySelector('.alert--success');
              });
              const ships = sectionInnerHTML.querySelector('[data-ld-ships]');
              if ( ships ) map._ships = ships.dataset.ldShips === 'true';
              window.LetsDishLocationInventory.cacheSet(variantId, map);
            }
            this.#renderPreview(sectionInnerHTML);
            document.dispatchEvent(new CustomEvent('ld:pickup-availability', { detail: { variantId: String(variantId) } }));
          })
          .catch(e => {
            console.log(e);
          });

      }

    }
    
    #bindDeliveryInfo() {
      const button = this.querySelector('.pickup-availability-info');
      if (!button || button.dataset.bound) return;
      button.dataset.bound = 'true';
      const panel = button.querySelector('.pickup-availability-info__panel');
      const place = () => {
        if (!panel) return;
        const icon = button.getBoundingClientRect();
        const margin = 12;
        const width = Math.min(264, window.innerWidth - margin * 2);
        let left = icon.left + (icon.width / 2) - (width / 2);
        left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
        const panelHeight = panel.offsetHeight || 120;
        let top = icon.top - panelHeight - 10;
        if (top < margin) top = Math.min(icon.bottom + 10, window.innerHeight - panelHeight - margin);
        panel.style.width = `${width}px`;
        panel.style.left = `${left}px`;
        panel.style.top = `${top}px`;
      };
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const open = button.classList.toggle('is-open');
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) place();
      });
      button.addEventListener('mouseenter', place);
      button.addEventListener('focus', place);
      window.addEventListener('resize', place);
      window.addEventListener('scroll', place, true);
      document.addEventListener('click', (event) => {
        if (!button.contains(event.target)) button.classList.remove('is-open');
      });
    }

    #renderPreview(sectionInnerHTML) {
      
      const inventory = window.LetsDishLocationInventory;
      const fulfillmentMode = inventory && inventory.getMode
        ? inventory.getMode()
        : (localStorage.getItem('fulfillment-mode') || 'pickup');
      const selectedStore = localStorage.getItem('selected-store');
      const deliveryStore = this.storeSelector?.dataset?.deliveryInventoryStore || 'lets-dish-av';
      const storeForCheck = inventory && inventory.getInventoryStore
        ? inventory.getInventoryStore()
        : (fulfillmentMode === 'delivery' ? deliveryStore : selectedStore);
      const inventoryHandles = inventory && inventory.resolveInventoryHandles
        ? inventory.resolveInventoryHandles(storeForCheck)
        : [storeForCheck];

      const deliverySubtitle = 'We ship to MN, WI, IA, ND, and SD';
      const deliveryDetails = 'Handmade ready-to-cook meals delivered to your door. We ship to Minnesota, Wisconsin, Iowa, North Dakota and South Dakota. $40 shipping for orders of $100–$225. Orders ship Monday through Thursday at 3pm.';

      const deliveryInfo = (available) => {
        if (!available) return '';
        return `<button type="button" class="pickup-availability-info" aria-label="Home delivery details"><span class="pickup-availability-info__icon" aria-hidden="true">i</span><span class="pickup-availability-info__panel" role="tooltip"><strong>${deliverySubtitle}</strong><span>${deliveryDetails}</span></span></button>`;
      };

      if (fulfillmentMode === 'delivery') {
        const alerts = sectionInnerHTML
          ? Array.from(sectionInnerHTML.querySelectorAll('.pickup-availability-alert[data-store]'))
          : [];
        const matched = alerts.find((elm) => {
          return inventoryHandles.includes(elm.dataset.store)
            || (inventory && inventory.isStoreInMap && inventory.isStoreInMap({ [elm.dataset.store]: true }, storeForCheck));
        });
        // Only Delivery-tagged products are stocked at AV Shipping.
        const notShippable = !!(sectionInnerHTML && sectionInnerHTML.querySelector('[data-ld-ships="false"]'));
        const available = !notShippable && !!(matched && matched.querySelector('.alert--success'));
        const text = available
          ? 'Available for home delivery. We ship to MN, WI, IA, ND, and SD.'
          : 'Not available for home delivery.';
        const tone = available ? 'alert--success' : 'alert--note';
        this.innerHTML = `<span class="alert alert--circle ${tone}">${text}${deliveryInfo(available)}</span>`;
        this.#bindDeliveryInfo();
        return;
      }

      const storesList = this.storeSelector?.storesList || {};
      const hasPickupStore = !!(selectedStore && storesList[selectedStore]);
      if ( this.storeSelector && storeForCheck && (hasPickupStore || fulfillmentMode === 'delivery') ) {
        let storeFound = false;
        sectionInnerHTML.querySelectorAll('.pickup-availability-alert').forEach(elm=>{
          const locationHandle = elm.dataset.store;
          const matched = inventoryHandles.includes(locationHandle)
            || (inventory && inventory.isStoreInMap && inventory.isStoreInMap({ [locationHandle]: true }, storeForCheck));
          if ( matched ) {
            const displayName = storesList[selectedStore] || storesList['apple-valley'] || storeForCheck;
            const available = !!elm.querySelector('.alert--success');
            let html = elm.innerHTML.replace('{{ store }}', displayName);
            if ( fulfillmentMode === 'delivery' ) {
              if ( available ) {
                html = html.replace(/Available for pickup at[\s\S]*?(<\/span>)/i, 'Available for home delivery. We ship to MN, WI, IA, ND, and SD.$1');
                html = html.replace('</span>', `${deliveryInfo(true)}</span>`);
              } else {
                html = html
                  .replace(/Out of stock at[\s\S]*?(<\/span>)/i, 'Not available for home delivery.$1')
                  .replace(/Not available for delivery from[\s\S]*?(<\/span>)/i, 'Not available for home delivery.$1');
              }
            }
            this.innerHTML = html;
            this.#bindDeliveryInfo();
            storeFound = true;
          }
        })
        if ( !storeFound ) {
          const displayName = storesList[selectedStore] || storesList['apple-valley'] || storeForCheck;
          let html = sectionInnerHTML.querySelector('.pickup-availability-alert[data-default-unavailable]').innerHTML.replace('{{ store }}', displayName);
          if ( fulfillmentMode === 'delivery' ) {
            html = html.replace(/Out of stock at[\s\S]*?(<\/span>)/i, 'Not available for home delivery.$1');
          }
          this.innerHTML = html;
          storeFound = true;
        }
        if ( !storeForCheck || !storeFound ) {
          this.innerHTML = '';
        }
      } else {
        this.innerHTML = sectionInnerHTML.querySelector('.pickup-availability-alert[data-default-store]').innerHTML;
      }

    }
    
  }

  if ( typeof customElements.get('pickup-availability-compact') === 'undefined' ) {
    customElements.define('pickup-availability-compact', PickupAvailabilityCompact);
	}

}